import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { authService } from './authService';

const SHOPS_COLLECTION = 'shops';

export const shopService = {
  // Create a new shop/merchant
  async createShop(shopData) {
    try {
      // 1. Create user in Firebase Authentication first
      const uid = await authService.createMerchantAuth(shopData.email, shopData.password);
      
      // 2. Create the shop document in Firestore with the uid as ownerId
      const shopRef = collection(db, SHOPS_COLLECTION);
      const docRef = await addDoc(shopRef, {
        ...shopData,
        ownerId: uid,
        createdAt: serverTimestamp(),
        stats: {
          totalScans: 0,
          totalUsers: 0,
          totalRewardsClaimed: 0
        },
        offers: []
      });
      return { id: docRef.id, ownerId: uid, ...shopData };
    } catch (error) {
      console.error("Error creating shop:", error);
      throw error;
    }
  },

  // Get all shops (for Super Admin)
  async getAllShops() {
    try {
      const shopRef = collection(db, SHOPS_COLLECTION);
      const querySnapshot = await getDocs(shopRef);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting all shops:", error);
      throw error;
    }
  },

  // Get shop by ID
  async getShopById(shopId) {
    try {
      const docRef = doc(db, SHOPS_COLLECTION, shopId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error("Error getting shop:", error);
      throw error;
    }
  },

  // Update shop campaigns (multiple sets of offers)
  async updateCampaigns(shopId, campaigns) {
    try {
      const docRef = doc(db, SHOPS_COLLECTION, shopId);
      await updateDoc(docRef, { campaigns });
    } catch (error) {
      console.error("Error updating campaigns:", error);
      throw error;
    }
  },

  // Update shop basic details (Super Admin)
  async updateShop(shopId, shopData) {
    try {
      const docRef = doc(db, SHOPS_COLLECTION, shopId);
      await updateDoc(docRef, { 
        ...shopData,
        updatedAt: serverTimestamp() 
      });
    } catch (error) {
      console.error("Error updating shop profile:", error);
      throw error;
    }
  },

  // Set which campaign is currently active
  async updateActiveCampaign(shopId, campaignIdx) {
    try {
      const docRef = doc(db, SHOPS_COLLECTION, shopId);
      await updateDoc(docRef, { activeCampaignIdx: campaignIdx });
    } catch (error) {
      console.error("Error updating active campaign:", error);
      throw error;
    }
  },

  // Delete a shop
  async deleteShop(shopId) {
    try {
      const docRef = doc(db, SHOPS_COLLECTION, shopId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting shop:", error);
      throw error;
    }
  },

  // Get the next sequential reward based on user's history
  async getRandomReward(shopId, userPhone, campaignIdx = null) {
    try {
      const shop = await this.getShopById(shopId);
      if (!shop || !shop.campaigns || shop.campaigns.length === 0) {
        return { name: 'Better luck next time!', icon: '🍀' };
      }

      // 1. Identify which campaigns we are considering
      let activeCampaigns = [];
      if (campaignIdx !== null && shop.campaigns[campaignIdx]) {
        activeCampaigns = [shop.campaigns[campaignIdx]];
      } else {
        activeCampaigns = shop.campaigns;
      }

      // 2. Flatten rewards for the target campaigns
      const targetRewards = [];
      activeCampaigns.forEach(campaign => {
        if (campaign.rewards && campaign.rewards.length > 0 && campaign.mode !== 'stamp') {
          campaign.rewards.forEach(reward => {
            targetRewards.push({
              ...reward,
              campaignName: campaign.name,
              campaignType: campaign.type
            });
          });
        }
      });

      if (targetRewards.length === 0) return null;

      // 3. Get user's scratch history count for THESE specific campaigns
      const campaignNames = activeCampaigns.map(c => c.name);
      const historyQ = query(
        collection(db, 'scratches'),
        where('userPhone', '==', userPhone),
        where('shopId', '==', shopId),
        where('campaignName', 'in', campaignNames)
      );
      const historySnap = await getDocs(historyQ);
      const scratchCount = historySnap.size;

      // 4. Check if user has already scratched everything in these campaigns
      if (scratchCount >= targetRewards.length) {
        return null; // All cards exhausted
      }

      // 5. Return the next reward in sequence
      return targetRewards[scratchCount];
    } catch (error) {
      console.error("Error picking reward:", error);
      return { name: 'Surprise Gift!', icon: '🎁' };
    }
  },

  // Get user's stamp progress for a specific campaign
  async getStampProgress(shopId, campaignIdx, userPhone) {
    try {
      const shop = await this.getShopById(shopId);
      if (!shop || !shop.campaigns || shop.campaigns[campaignIdx] === undefined) return null;
      
      const campaign = shop.campaigns[campaignIdx];
      
      const q = query(
        collection(db, 'scratches'),
        where('userPhone', '==', userPhone),
        where('shopId', '==', shopId),
        where('campaignName', '==', campaign.name)
      );
      
      const snap = await getDocs(q);
      const count = snap.size;
      
      return {
        count: count,
        total: campaign.totalStamps || 10,
        reward: campaign.finalReward,
        campaignName: campaign.name,
        shopName: shop.shopName,
        mode: campaign.mode,
        campaignIdx: campaignIdx
      };
    } catch (error) {
      console.error("Error getting stamp progress:", error);
      return null;
    }
  },

  // Save merchant inquiry from landing page
  async addInquiry(inquiryData) {
    try {
      const inquiryRef = collection(db, 'inquiries');
      const docRef = await addDoc(inquiryRef, {
        ...inquiryData,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error("Error adding inquiry:", error);
      throw error;
    }
  },

  // Get all inquiries (for Super Admin)
  async getAllInquiries() {
    try {
      const inquiryRef = collection(db, 'inquiries');
      const q = query(inquiryRef, where('status', '!=', 'deleted')); // Basic filter
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by createdAt descending (if serverTimestamp might be delayed, we sort client side or use firestore index)
      return data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    } catch (error) {
      console.error("Error getting all inquiries:", error);
      throw error;
    }
  },

  async getGlobalSettings() {
    try {
      const settingsDoc = await getDoc(doc(db, 'config', 'global'));
      if (settingsDoc.exists()) {
        return settingsDoc.data();
      }
      return null;
    } catch (error) {
      console.error("Error fetching global settings:", error);
      return null;
    }
  },

  async updateGlobalSettings(settingsData) {
    try {
      await setDoc(doc(db, 'config', 'global'), {
        ...settingsData,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return true;
    } catch (error) {
      console.error("Error updating global settings:", error);
      throw error;
    }
  }
};
